/**
 * @fileoverview Component for displaying a menu item.
 * This component includes a link to a specific route and displays an icon and title.
 */

import { PropsWithChildren, SVGProps } from 'react'
import { NavLink } from 'react-router-dom'
import * as css from './MenuItem.module.scss'


/**
 * @interface MenuItemProps
 * @description Props for the MenuItem component.
 * @property {string} to - The URL to link to.
 * @property {boolean} isActive - Whether the item is active.
 * @property {React.ComponentType<SVGProps<unknown>>} icon - The icon to display.
 * @property {string} title - The title to display.
 */
interface MenuItemProps extends PropsWithChildren {
    to: string
    isActive: boolean
    icon: React.ComponentType<SVGProps<unknown>>
    title: string
}

/**
 * @function classNames
 * @description Helper function to join class names with spaces.
 * @param {...string[]} classes - The class names to join.
 * @returns {string} - The joined class names.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

/**
 * @function MenuItem
 * @description Component for displaying a menu item.
 * @param {MenuItemProps} props - The props for the component.
 * @returns {JSX.Element} - A React component that renders the menu item.
 */
export function MenuItem(props: MenuItemProps) {
    return (
        <NavLink
            to={props.to}
            className={({ isActive }) => classNames(css.item, isActive ? css.active : '')}
        >
            <div className="flex items-center">
                <props.icon className={css.icon} />
                <span className={css.title}>{props.title}</span>
            </div>
        </NavLink>
    )
}
